import { computed, inject, Injectable, signal } from '@angular/core';
import { AppointmentStatusCode, PatientDetails, PracticeOperatory, Provider, SiteTreatment } from '@ffd/api-client';
import { AddTreatmentModalService } from '@libs/charting';
import { ActiveTranscriptionService } from '@libs/speech-recognition';
import {
  AppointmentReason,
  convertToHexColor,
  createAppointmentActions,
  getReadableTextColor,
  patientActions,
  patientsActions,
  ScheduleCalendarTime,
  selectNewAppointment,
  selectTeeth,
  TimePattern,
} from '@libs/store';
import { DropdownItem, PrefixData, Tooth } from '@libs/ui-lib';
import { DataState, Loadable } from '@libs/utils';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { NotesScreenService } from '@libs/notes';

/**
 * Prefix data structure for appointment status dropdown items.
 * Contains styling information for colored status badges.
 */
export interface AppointmentStatusPrefixData extends PrefixData {
  /** Hex color code for the badge background */
  backgroundColor: string;
  /** Calculated text color (black or white) for optimal contrast */
  textColor: string;
  /** Short status code to display in the badge */
  statusCode?: string;
}

export interface CreateAppointmentViewModel {
  allOperatoryOptions: DropdownItem<PracticeOperatory>[];
  selectedOperatory?: PracticeOperatory;
  appointmentStatusOptions: DropdownItem<AppointmentStatusCode>[];
  selectedAppointmentStatus?: AppointmentStatusCode;
  completedAppointmentStatus?: AppointmentStatusCode;
  treatments: Loadable<SiteTreatment[]>;
  notes: string;
  selectedProvider?: Provider;
  patient?: PatientDetails;
  reasonOptions: AppointmentReason[];
  selectedReasons: AppointmentReason[];
  isReasonsDataLoading: boolean;
  appointmentDate: DateTime;
  timeBlock: number;
  fromTime: ScheduleCalendarTime;
  toTime: ScheduleCalendarTime;
  appointmentLength: number;
  timePatterns: TimePattern[];
  teeth: Tooth[];
  isTentative?: boolean;
  isDirty?: boolean;
}

@Injectable()
export class CreateAppointmentService {
  private readonly store = inject(Store);
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);
  private readonly noteService = inject(NotesScreenService);
  private readonly activeTranscriptionService = inject(ActiveTranscriptionService);

  public viewModel = computed<CreateAppointmentViewModel>(() => {
    const state = this.store.selectSignal(selectNewAppointment)();
    const teeth = this.store.selectSignal(selectTeeth)()?.teethWithLayers || [];
    const treatments = this.addTreatmentModalService.selectSignal((state) => state)().treatments;

    return {
      ...state,
      appointmentDate: state.appointmentDate,
      patient: state.patient,
      providers: [],
      selectedProvider: state.selectedProvider,
      allOperatoryOptions: state.allOperatories?.map((operatory) => ({ value: operatory, label: operatory.name })) ?? [],
      selectedOperatory: state.operatory,
      completedAppointmentStatus: state.statusOptions.find((status) => {
        return status.description.toLowerCase().includes('completed');
      }),
      treatments,
      appointmentStatusOptions:
        state.statusOptions?.map((status) => {
          const statusColor = status.statusColor;
          if (!statusColor) {
            return {
              value: status,
              label: status.description,
              itemOverviewClass: 'status-dropdown-item',
              prefixData: null,
            };
          }

          const backgroundColor = convertToHexColor(statusColor);
          return {
            value: status,
            label: statusColor.statusName,
            itemOverviewClass: 'status-dropdown-item',
            prefixData: {
              backgroundColor,
              textColor: getReadableTextColor(backgroundColor),
              statusCode: statusColor.statusCode,
            } as AppointmentStatusPrefixData,
          };
        }) ?? [],
      selectedAppointmentStatus: state.selectedStatus,
      notes: state.notes,
      providersAreLoading: false,
      selectedReasons: state.selectedReasons,
      appointmentLength: state.appointmentLength,
      timePatterns: state.timePatterns,
      teeth,
      reasonOptions:
        treatments.data?.map((treatment) => ({
          code: treatment.dentalCode,
          name: treatment.ffdShortDescription ?? '',
          abbreviation: treatment.ffdAbbrevDescription ?? '',
        })) ?? [],
      isReasonsDataLoading: treatments.state === DataState.Loading,
    };
  });

  public transcriptionFormControl = computed(() => this.activeTranscriptionService.transcriptionControl);
  public transcriptionStatus = computed(() => this.activeTranscriptionService.status());
  public readonly activeDeviceId = computed(() => this.activeTranscriptionService.activeDeviceId());
  public lastFocusedCursorOffset = signal(0);

  public init(): void {
    // get treatments for reason selector
    this.store.dispatch(createAppointmentActions.onInit());
    this.lastFocusedCursorOffset.set(this.viewModel().notes.length);
  }

  public setOperatory(operatory: PracticeOperatory): void {
    this.store.dispatch(createAppointmentActions.onSetOperatory({ operatory }));
  }

  public setAppointmentStatus(status: AppointmentStatusCode): void {
    this.store.dispatch(createAppointmentActions.onSetStatus({ status }));
  }

  public setId(id: number): void {
    this.store.dispatch(createAppointmentActions.onSetId({ id }));
  }

  public setProvider(provider: Provider): void {
    this.store.dispatch(createAppointmentActions.onSetProvider({ provider }));
  }

  public setPatient(patient: PatientDetails, isInitialPatientSelect?: boolean): void {
    this.store.dispatch(createAppointmentActions.onSetPatient({ patient, isInitialPatientSelect }));
  }

  public loadTeeth(patient: PatientDetails, siteId: string): void {
    this.store.dispatch(patientActions.onLoadTeeth({ patientId: patient.id, siteId, examCount: 5, silentReload: true }));
  }

  public loadVisits(patient: PatientDetails, siteId: string): void {
    this.noteService.setPatientIdAndSiteId({ patientId: patient.id, siteId });
    this.noteService.init();
  }

  public setReasons(reasons: AppointmentReason[]): void {
    this.store.dispatch(createAppointmentActions.onSetReasons({ reasons }));
  }

  public setStartTime(fromTime: ScheduleCalendarTime): void {
    this.store.dispatch(createAppointmentActions.onSetStartTime({ fromTime }));
  }

  public setEndTime(toTime: ScheduleCalendarTime): void {
    this.store.dispatch(createAppointmentActions.onSetEndTime({ toTime }));
  }

  public setAppointmentLength(appointmentLength: number): void {
    this.store.dispatch(createAppointmentActions.onSetAppointmentLength({ appointmentLength }));
  }

  public setTimePatterns(timePatterns: TimePattern[]): void {
    this.store.dispatch(createAppointmentActions.onSetTimePatterns({ timePatterns }));
  }

  public setAppointmentDate(appointmentDate: DateTime): void {
    this.store.dispatch(createAppointmentActions.onSetAppointmentDate({ appointmentDate }));
  }

  public clearPatientSearchTerm(): void {
    this.store.dispatch(patientsActions.onClearSearchTerm());
  }

  public toggleSpeechRecognitionListening(notes: string): void {
    this.store.dispatch(createAppointmentActions.onSetNotes({ notes }));
    this.activeTranscriptionService.toggleTranscription({
      onStop: (result) => {
        const selectionStartOffset = this.lastFocusedCursorOffset();
        const newNote =
          selectionStartOffset === 0
            ? `${result.transcription}${notes}`
            : `${notes.slice(0, selectionStartOffset)}${result.transcription}${notes.slice(selectionStartOffset)}`;

        this.store.dispatch(createAppointmentActions.onSetNotes({ notes: newNote }));
        this.updateLastFocusedCursorOffset(selectionStartOffset + result.transcription.length);
      },
      onError: (error) => {
        console.error('Voice typing error:', error);
      },
    });
  }

  public setDeviceId(deviceId: string): void {
    // The active transcription service will handle device selection automatically
    this.activeTranscriptionService.selectMicrophone(deviceId);
  }

  public updateLastFocusedCursorOffset(offset: number): void {
    this.lastFocusedCursorOffset.set(offset);
  }
}
